import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import proj4 from "proj4";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { MapPin, Spline, Hexagon, MousePointer2, Undo2, Check, Trash2, Download, Save, Hammer, Table2, Loader2, ChevronDown, ChevronRight, Move, Tags } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getSchema, createFile } from "@/lib/api";

const ZONES = [54, 55, 56];
const mgaDef = (z) => `+proj=utm +zone=${z} +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`;
const BASEMAPS = [
  { id: "street", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", opts: { maxZoom: 19, subdomains: "abc" } },
  { id: "satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", opts: { maxZoom: 19 } },
];

const PIT_PRESETS = [
  { id: "{line}-{nn}", label: "SW1-01  ({line}-{nn})" },
  { id: "{line}.{n}", label: "SW1.1  ({line}.{n})" },
  { id: "{line}/{nn}", label: "SW1/01  ({line}/{nn})" },
  { id: "MH{nnn}", label: "MH001  (MH{nnn})" },
  { id: "{line}{nn}", label: "SW101  ({line}{nn})" },
];

const toMGA = (lat, lon, zone) => proj4(proj4.WGS84, mgaDef(zone), [lon, lat]);
const fromMGA = (e, n, zone) => { const [lon, lat] = proj4(mgaDef(zone), proj4.WGS84, [e, n]); return [lat, lon]; };

function lineLength(pts) { let d = 0; for (let i = 1; i < pts.length; i++) d += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]); return d; }
function polyArea(pts) { let a = 0; for (let i = 0; i < pts.length; i++) { const [x1, y1] = pts[i], [x2, y2] = pts[(i + 1) % pts.length]; a += x1 * y2 - x2 * y1; } return Math.abs(a) / 2; }

export default function BuilderPage() {
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [cls, setCls] = useState("Stormwater");
  const [feature, setFeature] = useState("Pit");
  const [zone, setZone] = useState(56);
  const [basemap, setBasemap] = useState("street");
  const [mode, setMode] = useState("point");
  const [features, setFeatures] = useState([]);
  const [selId, setSelId] = useState(null);
  const [cursorEN, setCursorEN] = useState(null);
  const [shiftLock, setShiftLock] = useState(false);
  const [openGroups, setOpenGroups] = useState({ Polygon: true, Line: true, Point: true });
  const [saving, setSaving] = useState(false);
  const [shiftE, setShiftE] = useState("");
  const [shiftN, setShiftN] = useState("");
  const dragItemR = useRef(null);
  // Fast naming (network draw)
  const [fastName, setFastName] = useState(false);
  const [lineName, setLineName] = useState("SW1");
  const [pitTpl, setPitTpl] = useState("{line}-{nn}");
  const [pipeTpl, setPipeTpl] = useState("{line}-PIPE-{nn}");
  const [startNum, setStartNum] = useState(1);
  const [pitFeat, setPitFeat] = useState("Pit");
  const [pipeFeat, setPipeFeat] = useState("Pipe");
  const fastNameR = useRef(false);
  const lineNameR = useRef("SW1");
  const pitTplR = useRef("{line}-{nn}");
  const pipeTplR = useRef("{line}-PIPE-{nn}");
  const pitFeatR = useRef("Pit");
  const pipeFeatR = useRef("Pipe");
  const fastCounterR = useRef(1);
  const fastPipeCounterR = useRef(1);
  const lastPitR = useRef(null);
  useEffect(() => { fastNameR.current = fastName; }, [fastName]);
  useEffect(() => { lineNameR.current = lineName; }, [lineName]);
  useEffect(() => { pitTplR.current = pitTpl; }, [pitTpl]);
  useEffect(() => { pipeTplR.current = pipeTpl; }, [pipeTpl]);
  useEffect(() => { pitFeatR.current = pitFeat; }, [pitFeat]);
  useEffect(() => { pipeFeatR.current = pipeFeat; }, [pipeFeat]);
  useEffect(() => {
    if (fastName) { fastCounterR.current = startNum; fastPipeCounterR.current = startNum; lastPitR.current = null; }
  }, [startNum, fastName]);

  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const baseRef = useRef(null);
  const featLayer = useRef(null);
  const draftLayer = useRef(null);
  const rubberRef = useRef(null);
  const snapRef = useRef(null);

  // mutable mirrors for leaflet handlers
  const modeR = useRef(mode);
  const draftR = useRef([]);
  const featR = useRef([]);
  const snappedR = useRef(null);
  const shiftR = useRef(false);
  const shiftTemplateR = useRef(null);
  const lastAttrsR = useRef({});
  const clsR = useRef(cls);
  const featureR = useRef(feature);
  const zoneR = useRef(zone);
  const schemaR = useRef(null);
  useEffect(() => { modeR.current = mode; }, [mode]);
  useEffect(() => { clsR.current = cls; }, [cls]);
  useEffect(() => { featureR.current = feature; }, [feature]);
  useEffect(() => { zoneR.current = zone; }, [zone]);

  useEffect(() => { getSchema().then(setSchema); }, []);
  useEffect(() => { schemaR.current = schema; }, [schema]);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "Shift" && !shiftR.current) {
        shiftR.current = true;
        const sel = featR.current.find((f) => f.id === selIdR.current);
        shiftTemplateR.current = sel ? { ...sel.attrs } : (lastAttrsR.current[featureR.current] ? { ...lastAttrsR.current[featureR.current] } : null);
        setShiftLock(true);
      }
    };
    const up = (e) => { if (e.key === "Shift") { shiftR.current = false; shiftTemplateR.current = null; setShiftLock(false); } };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false, doubleClickZoom: false, boxZoom: false }).setView([-24.866, 152.351], 17);
    baseRef.current = L.tileLayer(BASEMAPS[0].url, BASEMAPS[0].opts).addTo(map);
    featLayer.current = L.layerGroup().addTo(map);
    draftLayer.current = L.layerGroup().addTo(map);
    mapObj.current = map;
    map.on("mousemove", onMove);
    map.on("click", onClick);
    map.on("dblclick", finishDraft);
    map.on("contextmenu", (e) => { if (e.originalEvent) e.originalEvent.preventDefault(); if (fastNameR.current) endFastRun(); else finishDraft(); });
    setTimeout(() => map.invalidateSize(), 200);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!mapObj.current) return;
    const def = BASEMAPS.find((b) => b.id === basemap);
    if (baseRef.current) mapObj.current.removeLayer(baseRef.current);
    baseRef.current = L.tileLayer(def.url, def.opts).addTo(mapObj.current);
    baseRef.current.bringToBack();
  }, [basemap]);

  const allVertices = () => {
    const out = [];
    featR.current.forEach((f) => f.latlngs.forEach((ll) => out.push({ ll, id: f.attrs.ObjectId })));
    draftR.current.forEach((ll) => out.push({ ll, id: "draft" }));
    return out;
  };

  const computeSnap = (latlng) => {
    const map = mapObj.current;
    const cp = map.latLngToContainerPoint(latlng);
    let best = null, bestD = 13;
    allVertices().forEach((v) => {
      const p = map.latLngToContainerPoint(L.latLng(v.ll[0], v.ll[1]));
      const d = Math.hypot(p.x - cp.x, p.y - cp.y);
      if (d < bestD) { bestD = d; best = v; }
    });
    return best;
  };

  const onMove = (e) => {
    const map = mapObj.current;
    const snap = computeSnap(e.latlng);
    snappedR.current = snap;
    if (snapRef.current) { map.removeLayer(snapRef.current); snapRef.current = null; }
    const target = snap ? L.latLng(snap.ll[0], snap.ll[1]) : e.latlng;
    if (snap) {
      snapRef.current = L.circleMarker(target, { radius: 9, color: "#22d3ee", weight: 3, fill: false, className: "snap-pulse" }).addTo(map);
    }
    // rubber band
    if (rubberRef.current) { map.removeLayer(rubberRef.current); rubberRef.current = null; }
    if (fastNameR.current && lastPitR.current) {
      rubberRef.current = L.polyline([lastPitR.current.latlngs[0], [target.lat, target.lng]], { color: "#ef4444", weight: 2, className: "rubber-flash" }).addTo(map);
    } else if (draftR.current.length > 0 && (modeR.current === "line" || modeR.current === "polygon")) {
      const last = draftR.current[draftR.current.length - 1];
      rubberRef.current = L.polyline([last, [target.lat, target.lng]], { color: "#ef4444", weight: 2, className: "rubber-flash" }).addTo(map);
    }
    const [en, nn] = toMGA(target.lat, target.lng, zoneR.current);
    const next = fastNameR.current ? applyTpl(pitTplR.current, lineNameR.current, fastCounterR.current) : null;
    setCursorEN({ e: en.toFixed(2), n: nn.toFixed(2), snap: snap ? snap.id : null, next });
  };

  const finalize = (kind, latlngs) => {
    const seq = featR.current.filter((f) => f.attrs.InfrastructureCode === featureR.current).length + 1;
    const abbr = (featureR.current.match(/[A-Z]/g) || [featureR.current[0]]).join("").slice(0, 3).toUpperCase();
    const projected = latlngs.map(([la, lo]) => toMGA(la, lo, zoneR.current));
    const attrs = {
      ObjectId: `${abbr}-${String(seq).padStart(3, "0")}`,
      Status: "As Constructed",
      Owner: "Council",
      InfrastructureCode: featureR.current,
    };
    (schemaR.current?.classes?.[clsR.current]?.[featureR.current] || []).forEach((m) => { if (!(m in attrs)) attrs[m] = ""; });
    // Shift-lock: apply the captured template attributes to every new feature until Shift is released
    if (shiftR.current && shiftTemplateR.current) {
      Object.entries(shiftTemplateR.current).forEach(([k, v]) => {
        if (!["ObjectId", "Length_m", "Area_sqm"].includes(k)) attrs[k] = v;
      });
    }
    if (kind === "line") attrs.Length_m = lineLength(projected).toFixed(2);
    if (kind === "polygon") attrs.Area_sqm = polyArea(projected).toFixed(1);
    const f = { id: crypto.randomUUID(), kind, cls: clsR.current, feature: featureR.current, latlngs, attrs };
    lastAttrsR.current[featureR.current] = { ...attrs };
    featR.current = [...featR.current, f];
    setFeatures(featR.current);
    setSelId(f.id);
    draftR.current = [];
    redrawFeatures();
    redrawDraft();
    toast.success(`${featureR.current} created (${attrs.ObjectId})`);
  };

  const applyTpl = (tpl, line, n) => {
    const pad = (x, w) => String(x).padStart(w, "0");
    return (tpl || "").replace(/{line}/g, line).replace(/{nnn}/g, pad(n, 3)).replace(/{nn}/g, pad(n, 2)).replace(/{n}/g, String(n));
  };

  const addNamedFeature = (kind, feature, latlngs, objectId, extra) => {
    const attrs = { ObjectId: objectId, Status: "As Constructed", Owner: "Council", InfrastructureCode: feature };
    (schemaR.current?.classes?.[clsR.current]?.[feature] || []).forEach((m) => { if (!(m in attrs)) attrs[m] = ""; });
    Object.entries(extra || {}).forEach(([k, v]) => { attrs[k] = v; });
    const f = { id: crypto.randomUUID(), kind, cls: clsR.current, feature, latlngs, attrs };
    featR.current = [...featR.current, f];
    return f;
  };

  const handleFastClick = (ll) => {
    const line = lineNameR.current;
    const num = fastCounterR.current;
    const pitName = applyTpl(pitTplR.current, line, num);
    const pit = addNamedFeature("point", pitFeatR.current, [ll], pitName, { LineName: line, PitNumber: String(num) });
    if (lastPitR.current) {
      const pn = fastPipeCounterR.current;
      const pipeName = applyTpl(pipeTplR.current, line, pn);
      const pll = [lastPitR.current.latlngs[0], ll];
      const proj = pll.map(([la, lo]) => toMGA(la, lo, zoneR.current));
      addNamedFeature("line", pipeFeatR.current, pll, pipeName, {
        LineName: line, Length_m: lineLength(proj).toFixed(2),
        US_Pit: lastPitR.current.attrs.ObjectId, DS_Pit: pitName,
      });
      fastPipeCounterR.current = pn + 1;
    }
    lastPitR.current = pit;
    fastCounterR.current = num + 1;
    setFeatures(featR.current);
    setSelId(pit.id);
    redrawFeatures();
    toast.success(`Pit ${pitName} placed`);
  };

  const endFastRun = () => { lastPitR.current = null; toast.info("Run ended — next click starts a new run"); };

  const onClick = (e) => {
    if (fastNameR.current) {
      const snap = snappedR.current;
      const ll = snap ? snap.ll : [e.latlng.lat, e.latlng.lng];
      handleFastClick(ll);
      return;
    }
    const snap = snappedR.current;
    const ll = snap ? snap.ll : [e.latlng.lat, e.latlng.lng];
    const m = modeR.current;
    if (m === "point") { finalize("point", [ll]); return; }
    if (m === "line" || m === "polygon") {
      if (m === "polygon" && draftR.current.length >= 3) {
        const first = draftR.current[0];
        const fp = mapObj.current.latLngToContainerPoint(L.latLng(first[0], first[1]));
        const cp = mapObj.current.latLngToContainerPoint(e.latlng);
        if (Math.hypot(fp.x - cp.x, fp.y - cp.y) < 13) { finalize("polygon", [...draftR.current]); return; }
      }
      draftR.current = [...draftR.current, ll];
      redrawDraft();
    }
  };

  const finishDraft = () => {
    const m = modeR.current;
    let pts = draftR.current.slice();
    // dedupe trailing duplicate from dblclick
    if (pts.length >= 2) {
      const a = pts[pts.length - 1], b = pts[pts.length - 2];
      if (Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) pts = pts.slice(0, -1);
    }
    if (m === "line" && pts.length >= 2) finalize("line", pts);
    else if (m === "polygon" && pts.length >= 3) finalize("polygon", pts);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const item = dragItemR.current;
    dragItemR.current = null;
    if (!item || !mapObj.current) return;
    const latlng = mapObj.current.mouseEventToLatLng(e.nativeEvent);
    const ll = [latlng.lat, latlng.lng];
    clsR.current = item.cls; featureR.current = item.feature;
    setCls(item.cls); setFeature(item.feature);
    const m = GEOM_MODE[item.geom];
    modeR.current = m; setMode(m);
    if (item.geom === "Point") {
      finalize("point", [ll]);
    } else {
      draftR.current = [ll];
      redrawDraft();
      toast.info(item.geom === "Polygon" ? "Click to add vertices · right-click to close polygon" : "Click to add vertices · right-click to end line");
    }
  };

  const applyGlobalShift = () => {
    const dE = parseFloat(shiftE) || 0;
    const dN = parseFloat(shiftN) || 0;
    if (!dE && !dN) { toast.error("Enter a ΔE or ΔN to shift"); return; }
    if (featR.current.length === 0) { toast.error("Draw features first"); return; }
    featR.current = featR.current.map((f) => ({
      ...f,
      latlngs: f.latlngs.map(([la, lo]) => {
        const [e, n] = toMGA(la, lo, zoneR.current);
        return fromMGA(e + dE, n + dN, zoneR.current);
      }),
    }));
    setFeatures(featR.current);
    redrawFeatures();
    toast.success(`Shifted ${featR.current.length} feature(s) by ΔE ${dE} · ΔN ${dN} m`);
  };

  const redrawFeatures = () => {
    const lay = featLayer.current; lay.clearLayers();
    featR.current.forEach((f) => {
      const sel = f.id === selIdR.current;
      const col = sel ? "#2563eb" : "#0ea5e9";
      if (f.kind === "point") {
        L.circleMarker(f.latlngs[0], { radius: 7, color: "#fff", weight: 2, fillColor: col, fillOpacity: 0.95 }).addTo(lay)
          .bindTooltip(f.attrs.ObjectId, { permanent: false });
      } else if (f.kind === "line") {
        L.polyline(f.latlngs, { color: col, weight: 4 }).addTo(lay);
        const ds = f.latlngs[f.latlngs.length - 1];
        L.marker(ds, { icon: L.divIcon({ className: "", html: `<div style="background:#ef4444;color:#fff;font:600 9px monospace;padding:1px 4px;border-radius:2px">DS</div>`, iconSize: [22, 14] }) }).addTo(lay);
      } else {
        L.polygon(f.latlngs, { color: col, weight: 2, fillColor: col, fillOpacity: 0.25 }).addTo(lay)
          .bindTooltip(f.attrs.ObjectId);
      }
    });
  };

  const selIdR = useRef(selId);
  useEffect(() => { selIdR.current = selId; redrawFeatures(); /* eslint-disable-next-line */ }, [selId, features]);

  const redrawDraft = () => {
    const lay = draftLayer.current; if (!lay) return; lay.clearLayers();
    const pts = draftR.current;
    if (pts.length > 1) L.polyline(pts, { color: "#ef4444", weight: 2, dashArray: "6 6" }).addTo(lay);
    pts.forEach((p, i) => {
      L.circleMarker(p, { radius: i === 0 ? 6 : 4, color: i === 0 ? "#22d3ee" : "#ef4444", weight: 2, fillColor: "#0a0a0a", fillOpacity: 1 }).addTo(lay);
    });
  };

  const undoVertex = () => { draftR.current = draftR.current.slice(0, -1); redrawDraft(); };
  const clearAll = () => {
    featR.current = []; draftR.current = []; setFeatures([]); setSelId(null);
    redrawFeatures(); redrawDraft();
  };
  const removeFeature = (id) => {
    featR.current = featR.current.filter((f) => f.id !== id); setFeatures(featR.current);
    if (selId === id) setSelId(null); redrawFeatures();
  };
  const updateAttr = (id, k, v) => {
    featR.current = featR.current.map((f) => f.id === id ? { ...f, attrs: { ...f.attrs, [k]: v } } : f);
    setFeatures(featR.current);
    const f = featR.current.find((x) => x.id === id);
    if (f) lastAttrsR.current[f.feature] = { ...f.attrs };
  };

  const buildXML = () => {
    const byClass = {};
    featR.current.forEach((f) => { (byClass[f.cls] = byClass[f.cls] || []).push(f); });
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<ADAC xmlns="http://www.ipweaq.com/ADAC" schemaVersion="5.0.1">\n`;
    xml += `  <GlobalAttributes>\n    <Datum>GDA2020 MGA Zone ${zone} / AHD</Datum>\n    <DataSource>ADAC Builder</DataSource>\n  </GlobalAttributes>\n`;
    Object.entries(byClass).forEach(([c, feats]) => {
      xml += `  <${c}>\n`;
      feats.forEach((f) => {
        const [e, n] = toMGA(f.latlngs[0][0], f.latlngs[0][1], zone);
        const geom = f.kind === "point" ? "Point" : f.kind === "line" ? "Line" : "Polygon";
        xml += `    <${f.feature}>\n`;
        xml += `      <Geometry>${geom}</Geometry>\n      <Easting_m>${e.toFixed(3)}</Easting_m>\n      <Northing_m>${n.toFixed(3)}</Northing_m>\n`;
        Object.entries(f.attrs).forEach(([k, v]) => { xml += `      <${k}>${String(v).replace(/[<>&]/g, "")}</${k}>\n`; });
        xml += `    </${f.feature}>\n`;
      });
      xml += `  </${c}>\n`;
    });
    xml += `</ADAC>\n`;
    return xml;
  };

  const download = () => {
    const blob = new Blob([buildXML()], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "ADAC_Build.xml"; a.click();
    URL.revokeObjectURL(url);
  };
  const saveToLibrary = async () => {
    if (featR.current.length === 0) { toast.error("Draw at least one feature"); return; }
    setSaving(true);
    try {
      await createFile(`ADAC_Build_${new Date().toISOString().slice(0, 10)}.xml`, buildXML());
      toast.success("ADAC XML saved to Library");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const classList = schema ? Object.keys(schema.classes) : [];
  const featureList = schema && schema.classes[cls] ? Object.keys(schema.classes[cls]) : [];
  const selected = features.find((f) => f.id === selId);

  const inferGeom = (f, mand) => {
    const m = (mand || []).join(" ").toLowerCase();
    const nl = f.toLowerCase();
    if (m.includes("area") || /(area|reserve|lot|easement|pavement|parking|island|landscape|openspace|wsud)/.test(nl)) return "Polygon";
    if (m.includes("length") || m.includes("us_") || /(pipe|drain|conduit|barriercontinuous|pathway|pathstructure|roadedge|roadpathway|connection|contour|subsoil)/.test(nl)) return "Line";
    return "Point";
  };
  const legendGroups = { Polygon: [], Line: [], Point: [] };
  if (schema) Object.entries(schema.classes).forEach(([c, feats]) => Object.entries(feats).forEach(([f, mand]) => legendGroups[inferGeom(f, mand)].push({ cls: c, feature: f })));
  Object.values(legendGroups).forEach((arr) => arr.sort((a, b) => (a.cls + a.feature).localeCompare(b.cls + b.feature)));
  const GEOM_ORDER = ["Polygon", "Line", "Point"];
  const GEOM_MODE = { Polygon: "polygon", Line: "line", Point: "point" };
  const GEOM_ICON = { Polygon: Hexagon, Line: Spline, Point: MapPin };
  const pickLegend = (c, f, g) => { setCls(c); setFeature(f); setMode(GEOM_MODE[g]); };
  const toggleGroup = (g) => setOpenGroups((o) => ({ ...o, [g]: !o[g] }));

  const TOOLS = [
    { id: "select", icon: MousePointer2, label: "Pan" },
    { id: "point", icon: MapPin, label: "Point" },
    { id: "line", icon: Spline, label: "Line" },
    { id: "polygon", icon: Hexagon, label: "Polygon" },
  ];

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-[1100]">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Builder</h1>
          <p className="font-mono text-[11px] text-zinc-500">Create ADAC XML from scratch or non-ADAC data</p>
        </div>
      </header>

      <div className="p-6">
        <Tabs defaultValue="draw">
          <TabsList className="bg-[#101010] border border-zinc-800 rounded-sm p-1 mb-4">
            <TabsTrigger value="draw" data-testid="tab-draw" className="rounded-sm font-mono text-xs data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
              <Hammer className="w-3.5 h-3.5 mr-1.5" /> Draw on Map
            </TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import" className="rounded-sm font-mono text-xs data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
              <Table2 className="w-3.5 h-3.5 mr-1.5" /> Import CSV
            </TabsTrigger>
          </TabsList>

          <TabsContent value="draw">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-sm border border-zinc-800 bg-[#101010]">
              <div className="flex rounded-sm overflow-hidden border border-zinc-700">
                {TOOLS.map((t) => (
                  <button key={t.id} data-testid={`tool-${t.id}`} onClick={() => setMode(t.id)}
                    className={`px-3 py-1.5 font-mono text-[11px] flex items-center gap-1.5 ${mode === t.id ? "bg-blue-600 text-white" : "text-zinc-400 hover:bg-zinc-800"}`}>
                    <t.icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                ))}
              </div>
              <Select value={cls} onValueChange={(v) => { setCls(v); const fl = Object.keys(schema.classes[v]); setFeature(fl[0]); }}>
                <SelectTrigger data-testid="class-select" className="w-40 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">
                  {classList.map((c) => <SelectItem key={c} value={c} className="font-mono text-xs">{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={feature} onValueChange={setFeature}>
                <SelectTrigger data-testid="feature-select" className="w-44 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">
                  {featureList.map((f) => <SelectItem key={f} value={f} className="font-mono text-xs">{f}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(zone)} onValueChange={(v) => setZone(parseInt(v))}>
                <SelectTrigger className="w-28 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                  {ZONES.map((z) => <SelectItem key={z} value={String(z)} className="font-mono text-xs">Zone {z}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex rounded-sm overflow-hidden border border-zinc-700">
                {BASEMAPS.map((b) => (
                  <button key={b.id} onClick={() => setBasemap(b.id)} className={`px-3 py-1.5 font-mono text-[11px] capitalize ${basemap === b.id ? "bg-blue-600 text-white" : "text-zinc-400 hover:bg-zinc-800"}`}>{b.id}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-sm border border-zinc-700 bg-[#0A0A0A]" data-testid="global-shift">
                <span className="font-mono text-[10px] text-zinc-500">Shift all</span>
                <Input value={shiftE} onChange={(e) => setShiftE(e.target.value)} placeholder="ΔE" data-testid="shift-e" className="h-7 w-16 rounded-sm bg-[#121212] border-zinc-700 font-mono text-[11px]" />
                <Input value={shiftN} onChange={(e) => setShiftN(e.target.value)} placeholder="ΔN" data-testid="shift-n" className="h-7 w-16 rounded-sm bg-[#121212] border-zinc-700 font-mono text-[11px]" />
                <Button size="sm" data-testid="apply-shift-btn" onClick={applyGlobalShift} className="h-7 rounded-sm bg-blue-600 hover:bg-blue-500 text-white font-mono text-[11px]"><Move className="w-3.5 h-3.5 mr-1" /> Apply</Button>
              </div>
              <div className="flex items-center gap-1 ml-auto">
                <Button size="sm" variant="outline" data-testid="undo-btn" onClick={undoVertex} className="h-9 rounded-sm border-zinc-700 bg-transparent text-zinc-300"><Undo2 className="w-3.5 h-3.5 mr-1" /> Undo</Button>
                <Button size="sm" variant="outline" data-testid="finish-btn" onClick={finishDraft} className="h-9 rounded-sm border-zinc-700 bg-transparent text-zinc-300"><Check className="w-3.5 h-3.5 mr-1" /> Finish</Button>
                <Button size="sm" variant="outline" data-testid="clear-btn" onClick={clearAll} className="h-9 rounded-sm border-zinc-700 bg-transparent text-zinc-300"><Trash2 className="w-3.5 h-3.5 mr-1" /> Clear</Button>
              </div>
            </div>

            <div className={`flex flex-wrap items-center gap-2 mb-4 p-2 rounded-sm border ${fastName ? "border-emerald-500/40 bg-emerald-500/5" : "border-zinc-800 bg-[#0C0C0C]"}`} data-testid="fast-naming-bar">
              <button data-testid="fast-name-toggle" onClick={() => setFastName((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono text-[11px] border transition-colors duration-75 ${fastName ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-400" : "border-zinc-700 text-zinc-400 hover:bg-zinc-900"}`}>
                <Tags className="w-3.5 h-3.5" /> Fast naming {fastName ? "ON" : "OFF"}
              </button>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[10px] text-zinc-500">Line</span>
                <Input value={lineName} onChange={(e) => setLineName(e.target.value)} data-testid="line-name" className="h-7 w-20 rounded-sm bg-[#121212] border-zinc-700 font-mono text-[11px]" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[10px] text-zinc-500">Pit</span>
                <Select value={pitTpl} onValueChange={setPitTpl}>
                  <SelectTrigger data-testid="pit-convention" className="h-7 w-40 rounded-sm bg-[#121212] border-zinc-700 font-mono text-[11px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                    {PIT_PRESETS.map((p) => <SelectItem key={p.id} value={p.id} className="font-mono text-[11px]">{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={pitTpl} onChange={(e) => setPitTpl(e.target.value)} data-testid="pit-template" title="Custom template: {line} {n} {nn} {nnn}" className="h-7 w-28 rounded-sm bg-[#121212] border-zinc-700 font-mono text-[11px]" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[10px] text-zinc-500">Start#</span>
                <Input type="number" value={startNum} onChange={(e) => setStartNum(parseInt(e.target.value) || 1)} data-testid="start-num" className="h-7 w-14 rounded-sm bg-[#121212] border-zinc-700 font-mono text-[11px]" />
              </div>
              <div className="flex items-center gap-1.5">
                <Select value={pitFeat} onValueChange={setPitFeat}>
                  <SelectTrigger data-testid="pit-feature" className="h-7 w-32 rounded-sm bg-[#121212] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Pit type" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">{featureList.map((f) => <SelectItem key={f} value={f} className="font-mono text-[11px]">{f}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={pipeFeat} onValueChange={setPipeFeat}>
                  <SelectTrigger data-testid="pipe-feature" className="h-7 w-32 rounded-sm bg-[#121212] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Pipe type" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">{featureList.map((f) => <SelectItem key={f} value={f} className="font-mono text-[11px]">{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {fastName && <span className="font-mono text-[10px] text-emerald-400/80 ml-auto">Click = drop named pit + pipe · right-click = end run</span>}
            </div>


            <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_330px] gap-4">
              {/* ADAC object legend — collapsible, grouped Polygons → Lines → Points */}
              <div className="border border-zinc-800 rounded-sm bg-[#101010] overflow-auto" style={{ maxHeight: "64vh" }} data-testid="adac-legend">
                <div className="px-3 py-2 border-b border-zinc-800 font-mono text-[11px] uppercase tracking-wider text-zinc-500 sticky top-0 bg-[#101010]">ADAC Objects</div>
                {GEOM_ORDER.map((g) => {
                  const GIcon = GEOM_ICON[g];
                  const open = openGroups[g];
                  return (
                    <div key={g}>
                      <button data-testid={`legend-group-${g}`} onClick={() => toggleGroup(g)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-[#0C0C0C] border-y border-zinc-900 hover:bg-zinc-900">
                        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-300">
                          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          <GIcon className="w-3.5 h-3.5" /> {g}s
                        </span>
                        <span className="font-mono text-[10px] text-zinc-600">{legendGroups[g].length}</span>
                      </button>
                      {open && legendGroups[g].map((it) => (
                        <button key={it.cls + it.feature} data-testid={`legend-${it.feature}`}
                          draggable
                          onDragStart={() => { dragItemR.current = { cls: it.cls, feature: it.feature, geom: g }; }}
                          onClick={() => pickLegend(it.cls, it.feature, g)}
                          className={`w-full text-left px-3 py-1.5 font-mono text-[11px] flex items-center justify-between cursor-grab active:cursor-grabbing ${cls === it.cls && feature === it.feature ? "bg-blue-600/15 text-blue-400" : "text-zinc-400 hover:bg-zinc-900"}`}>
                          <span>{it.feature}</span>
                          <span className="text-[9px] text-zinc-600">{it.cls.slice(0, 4)}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>

              <div className="relative border border-zinc-800 rounded-sm overflow-hidden" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
                <div ref={mapRef} data-testid="builder-map" style={{ height: "64vh", background: "#e5e7eb" }} />
                {cursorEN && (
                  <div className="absolute bottom-3 left-3 z-[1000] font-mono text-[11px] bg-[#121212]/95 border border-zinc-800 rounded-sm px-3 py-1.5 text-zinc-300">
                    E {cursorEN.e} · N {cursorEN.n}
                    {cursorEN.snap && <span className="text-cyan-400"> · snap → {cursorEN.snap}</span>}
                    {cursorEN.next && <span className="text-emerald-400"> · next → {cursorEN.next}</span>}
                  </div>
                )}
                <div className="absolute top-3 left-3 z-[1000] font-mono text-[11px] bg-[#121212]/95 border border-zinc-800 rounded-sm px-3 py-1.5 text-zinc-400">
                  <span className="text-zinc-300">{cls} → {feature}</span>
                  {" · "}
                  {mode === "point" && "Click to place"}
                  {mode === "line" && "Click vertices · right-click/dbl-click to end · last = downstream"}
                  {mode === "polygon" && "Click vertices · right-click or click 1st point to close"}
                  {mode === "select" && "Pan & zoom"}
                  {" · "}<span className={shiftLock ? "text-emerald-400" : "text-amber-400"}>{shiftLock ? "SHIFT LOCK ON — attributes carry to all new features" : "Hold Shift = lock current attributes onto new features"}</span>
                </div>
              </div>

              {/* Attribute panel */}
              <div className="border border-zinc-800 rounded-sm bg-[#101010] flex flex-col" style={{ maxHeight: "64vh" }}>
                <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Features ({features.length})</span>
                  <div className="flex gap-1">
                    <Button size="sm" data-testid="download-btn" onClick={download} disabled={!features.length} className="h-7 rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200"><Download className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" data-testid="save-build-btn" onClick={saveToLibrary} disabled={saving || !features.length} className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
                    </Button>
                  </div>
                </div>
                <div className="overflow-auto flex-1">
                  {features.length === 0 ? (
                    <p className="font-mono text-[11px] text-zinc-600 p-4">Draw features on the map to add them here.</p>
                  ) : (
                    <div className="divide-y divide-zinc-900">
                      {features.map((f) => (
                        <div key={f.id} data-testid={`feature-${f.attrs.ObjectId}`}>
                          <button onClick={() => setSelId(selId === f.id ? null : f.id)} className={`w-full text-left px-4 py-2 flex items-center justify-between ${selId === f.id ? "bg-blue-600/10" : "hover:bg-zinc-900/50"}`}>
                            <span className="font-mono text-xs text-zinc-200">{f.attrs.ObjectId}</span>
                            <span className="font-mono text-[10px] text-zinc-500">{f.feature} · {f.kind}</span>
                          </button>
                          {selId === f.id && (
                            <div className="px-4 py-3 space-y-2 bg-[#0C0C0C]">
                              {Object.entries(f.attrs).map(([k, v]) => (
                                <div key={k} className="flex items-center gap-2">
                                  <label className="font-mono text-[10px] text-zinc-500 w-28 shrink-0 truncate">{k}</label>
                                  <Input value={v} onChange={(e) => updateAttr(f.id, k, e.target.value)} data-testid={`attr-${k}`} className="h-7 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]" />
                                </div>
                              ))}
                              <Button size="sm" variant="ghost" onClick={() => removeFeature(f.id)} className="h-7 rounded-sm text-red-400 hover:bg-zinc-800 w-full"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete feature</Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="import">
            <CsvImport schema={schema} onSaved={() => navigate("/")} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function CsvImport({ schema, onSaved }) {
  const [text, setText] = useState("");
  const [cls, setCls] = useState("Stormwater");
  const [feature, setFeature] = useState("Pit");
  const [zone, setZone] = useState(56);
  const [coordMode, setCoordMode] = useState("en");
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [saving, setSaving] = useState(false);

  const adacFields = schema ? ["ObjectId", "Status", "Owner", "InfrastructureCode", coordMode === "en" ? "Easting_m" : "Latitude", coordMode === "en" ? "Northing_m" : "Longitude", ...(schema.classes[cls]?.[feature] || [])] : [];

  const parse = () => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) { toast.error("Paste CSV with a header row + data"); return; }
    const hs = lines[0].split(",").map((h) => h.trim());
    const rs = lines.slice(1).map((l) => l.split(",").map((c) => c.trim()));
    setHeaders(hs); setRows(rs);
    // auto-guess mapping
    const guess = {};
    adacFields.forEach((f) => {
      const hit = hs.find((h) => h.toLowerCase().replace(/[^a-z0-9]/g, "") === f.toLowerCase().replace(/[^a-z0-9]/g, ""));
      if (hit) guess[f] = hit;
    });
    setMapping(guess);
    toast.success(`${rs.length} rows parsed`);
  };

  const buildXML = () => {
    const idx = (h) => headers.indexOf(h);
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<ADAC xmlns="http://www.ipweaq.com/ADAC" schemaVersion="5.0.1">\n  <GlobalAttributes>\n    <Datum>GDA2020 MGA Zone ${zone} / AHD</Datum>\n    <DataSource>ADAC Builder CSV import</DataSource>\n  </GlobalAttributes>\n  <${cls}>\n`;
    rows.forEach((r, i) => {
      xml += `    <${feature}>\n      <Geometry>Point</Geometry>\n`;
      let e = "", n = "";
      if (coordMode === "en") {
        e = mapping.Easting_m ? r[idx(mapping.Easting_m)] : "";
        n = mapping.Northing_m ? r[idx(mapping.Northing_m)] : "";
      } else {
        const la = parseFloat(mapping.Latitude ? r[idx(mapping.Latitude)] : "");
        const lo = parseFloat(mapping.Longitude ? r[idx(mapping.Longitude)] : "");
        if (!isNaN(la) && !isNaN(lo)) { const [E, N] = toMGA(la, lo, zone); e = E.toFixed(3); n = N.toFixed(3); }
      }
      xml += `      <Easting_m>${e}</Easting_m>\n      <Northing_m>${n}</Northing_m>\n`;
      adacFields.filter((f) => !["Easting_m", "Northing_m", "Latitude", "Longitude"].includes(f)).forEach((f) => {
        let v = mapping[f] ? r[idx(mapping[f])] : "";
        if (!v) { if (f === "Status") v = "As Constructed"; else if (f === "Owner") v = "Council"; else if (f === "InfrastructureCode") v = feature; else if (f === "ObjectId") v = `${feature.slice(0, 3).toUpperCase()}-${String(i + 1).padStart(3, "0")}`; }
        xml += `      <${f}>${String(v).replace(/[<>&]/g, "")}</${f}>\n`;
      });
      xml += `    </${feature}>\n`;
    });
    xml += `  </${cls}>\n</ADAC>\n`;
    return xml;
  };

  const save = async () => {
    if (!rows.length) { toast.error("Parse a CSV first"); return; }
    setSaving(true);
    try { await createFile(`ADAC_Import_${Date.now()}.xml`, buildXML()); toast.success("Imported & saved to Library"); onSaved?.(); }
    catch { toast.error("Import failed"); } finally { setSaving(false); }
  };

  const classList = schema ? Object.keys(schema.classes) : [];
  const featureList = schema && schema.classes[cls] ? Object.keys(schema.classes[cls]) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 p-3 rounded-sm border border-zinc-800 bg-[#101010]">
        <Select value={cls} onValueChange={(v) => { setCls(v); setFeature(Object.keys(schema.classes[v])[0]); }}>
          <SelectTrigger data-testid="csv-class" className="w-40 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">{classList.map((c) => <SelectItem key={c} value={c} className="font-mono text-xs">{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={feature} onValueChange={setFeature}>
          <SelectTrigger data-testid="csv-feature" className="w-44 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">{featureList.map((f) => <SelectItem key={f} value={f} className="font-mono text-xs">{f}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={coordMode} onValueChange={setCoordMode}>
          <SelectTrigger className="w-40 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
            <SelectItem value="en" className="font-mono text-xs">Easting/Northing</SelectItem>
            <SelectItem value="ll" className="font-mono text-xs">Lat/Lon</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(zone)} onValueChange={(v) => setZone(parseInt(v))}>
          <SelectTrigger className="w-28 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">{ZONES.map((z) => <SelectItem key={z} value={String(z)} className="font-mono text-xs">Zone {z}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <textarea data-testid="csv-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="ObjectId,Status,Easting_m,Northing_m,SurfaceLevel_m,InvertLevel_m&#10;SW-PIT-001,As Constructed,434120.35,7250410.22,14.12,12.34"
        className="w-full h-40 rounded-sm bg-[#0A0A0A] border border-zinc-700 font-mono text-xs p-3 resize-none text-zinc-200" />
      <div className="flex gap-2">
        <Button data-testid="csv-parse" onClick={parse} className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white">Parse & auto-map</Button>
        <Button data-testid="csv-save" onClick={save} disabled={saving || !rows.length} className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Generate & save
        </Button>
      </div>

      {headers.length > 0 && (
        <div className="border border-zinc-800 rounded-sm overflow-hidden">
          <div className="px-4 py-2 bg-[#101010] border-b border-zinc-800 font-mono text-[11px] uppercase tracking-wider text-zinc-500">Column mapping ({rows.length} rows)</div>
          <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-3">
            {adacFields.map((f) => (
              <div key={f} className="flex items-center gap-2">
                <label className="font-mono text-[10px] text-zinc-400 w-24 truncate shrink-0">{f}</label>
                <Select value={mapping[f] || "__none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f]: v === "__none" ? undefined : v }))}>
                  <SelectTrigger data-testid={`map-${f}`} className="rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px] h-8"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                    <SelectItem value="__none" className="font-mono text-xs">— none —</SelectItem>
                    {headers.map((h) => <SelectItem key={h} value={h} className="font-mono text-xs">{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
